import type { SupabaseClient } from '@supabase/supabase-js';
import { DEFAULT_EVENT_IMAGE } from '@/constants/defaultImages';

const BUCKET = 'event-images';

/**
 * Parses an `events.image` value into a storage path within the `event-images` bucket.
 *
 * Handles two formats that may exist in the DB:
 *   1. Full public URL (legacy): https://....supabase.co/storage/v1/object/public/event-images/userId/file.jpg
 *   2. Path-only (new uploads):  userId/file.jpg
 */
export function parseEventImagesStoragePath(stored: string | null | undefined): string | null {
  if (!stored || !stored.trim()) return null;

  // Full URL containing the bucket name
  const match = stored.match(/event-images\/(.+)$/);
  if (match?.[1]) return match[1];

  // Already a bare path (no slashes at the start)
  if (!stored.startsWith('http') && stored.includes('/')) return stored;

  return null;
}

/**
 * Returns a signed URL for a stored `events.image` value.
 *
 * @param client        Supabase client (must be authenticated for private buckets)
 * @param stored        The value in `events.image` — full URL or path
 * @param expiresIn     Seconds before the signed URL expires (default 1 hour)
 * @returns             Signed URL string, or `DEFAULT_EVENT_IMAGE` if unavailable
 */
export async function getSignedEventImageUrl(
  client: SupabaseClient,
  stored: string | null | undefined,
  expiresIn = 3600,
): Promise<string> {
  const path = parseEventImagesStoragePath(stored);
  if (!path) return DEFAULT_EVENT_IMAGE;

  const { data, error } = await client.storage
    .from(BUCKET)
    .createSignedUrl(path, expiresIn);

  if (error || !data?.signedUrl) return DEFAULT_EVENT_IMAGE;
  return data.signedUrl;
}
